import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { preparePdfBlobForOutput } from "utils/pdf/preparePdfBlobForOutput";

const UUID_PREFIX_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/;

const decodeRepeatedly = (value: string): string => {
  let decoded = value;

  for (let i = 0; i < 2; i += 1) {
    try {
      const nextDecoded = decodeURIComponent(decoded);
      if (nextDecoded === decoded) {
        break;
      }
      decoded = nextDecoded;
    } catch {
      break;
    }
  }

  return decoded;
};

const extractFileNameFromUrl = (fileUrl: string): string => {
  try {
    const pathName = new URL(fileUrl).pathname;
    const fileNameWithPrefix = pathName.split("/").pop() || "document.pdf";

    return decodeRepeatedly(fileNameWithPrefix).replace(UUID_PREFIX_REGEX, "");
  } catch {
    return "document.pdf";
  }
};

const normalizeTitle = (title: string | null, fileUrl: string): string => {
  const sanitizedTitle = title?.trim();

  if (sanitizedTitle) {
    return sanitizedTitle.toLowerCase().endsWith(".pdf")
      ? sanitizedTitle
      : `${sanitizedTitle}.pdf`;
  }

  const fileName = extractFileNameFromUrl(fileUrl);
  return fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;
};

const PdfPreviewPage = () => {
  const [searchParams] = useSearchParams();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fileUrl = searchParams.get("fileUrl");
    const title = searchParams.get("title");
    const attachmentUrls = searchParams.getAll("attachmentUrl");

    if (!fileUrl) {
      setHasError(true);
      return;
    }

    const normalizedTitle = normalizeTitle(title, fileUrl);
    document.title = normalizedTitle;

    let isCancelled = false;

    const openPreview = async () => {
      try {
        const pdfBlob = await preparePdfBlobForOutput({
          fileUrl,
          attachmentUrls,
          title: normalizedTitle,
        });
        const previewBlobUrl = URL.createObjectURL(pdfBlob);

        if (isCancelled) {
          URL.revokeObjectURL(previewBlobUrl);
          return;
        }

        window.location.replace(previewBlobUrl);
      } catch (error) {
        console.error("PdfPreviewPage failed:", error);

        if (!isCancelled) {
          setHasError(true);
        }
      }
    };

    void openPreview();

    return () => {
      isCancelled = true;
    };
  }, [searchParams]);

  if (hasError) {
    return <ErrorMessage>PDF 미리보기에 실패했습니다.</ErrorMessage>;
  }

  return <PreviewScreen />;
};

export default PdfPreviewPage;

const PreviewScreen = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const ErrorMessage = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fff;
  color: #202020;
`;
